import { useState, useEffect, useCallback, useRef } from "react";
import { api } from "../services/api";
import { Lobby } from "../services/api/LobbyApi";
import { useSocket } from "./useSocket";
import { useUser } from "../context/UserContext";

export interface DiscordError {
  code: "CHANNEL_CREATION_FAILED" | "DISCORD_BOT_UNAVAILABLE" | "PLAYERS_MISSING_DISCORD" | "GUILD_NOT_CONFIGURED";
  message: string;
  affectedUserIds?: string[];
}

export interface ValidationFailure {
  reason: "MISSING_DISCORD" | "ACTIVE_MATCH_EXISTS";
  affectedUserIds: string[];
}

export interface UseLobbyReturn {
  lobby: Lobby | null;
  loading: boolean;
  error: string | null;
  isHost: boolean;
  discordError: DiscordError | null;
  clearDiscordError: () => void;
  validationFailure: ValidationFailure | null;
  clearValidationFailure: () => void;
  refresh: () => Promise<void>;
}

export function useLobby(): UseLobbyReturn {
  const [lobby, setLobby] = useState<Lobby | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [discordError, setDiscordError] = useState<DiscordError | null>(null);
  const [validationFailure, setValidationFailure] = useState<ValidationFailure | null>(null);
  const { subscribe, emit, connected } = useSocket();
  const { user } = useUser();
  const versionRef = useRef(0);

  const isHost = Boolean(lobby && user && lobby.hostId === user.userId);

  const clearDiscordError = useCallback(() => {
    setDiscordError(null);
  }, []);

  const clearValidationFailure = useCallback(() => {
    setValidationFailure(null);
  }, []);

  const refresh = useCallback(async () => {
    try {
      setError(null);
      const result = await api.lobby.getMyLobby();
      if (result) {
        versionRef.current = result.version || 0;
      } else {
        versionRef.current = 0;
      }
      setLobby(result);
    } catch (err: any) {
      setError(err.message || "Failed to fetch lobby");
      setLobby(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on mount
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Join lobby room when we have a lobby and socket is connected
  useEffect(() => {
    if (!lobby?.lobbyId || !connected) return;
    emit("join_lobby", { lobbyId: lobby.lobbyId });
    return () => {
      emit("leave_lobby", { lobbyId: lobby.lobbyId });
    };
  }, [lobby?.lobbyId, connected, emit]);

  // Reconnect recovery — refetch on reconnect
  useEffect(() => {
    if (connected) {
      refresh();
    }
  }, [connected]);

  // Listen for LOBBY_UPDATED signal — refetch if version is newer
  useEffect(() => {
    const unsub = subscribe("LOBBY_UPDATED", (data: { lobbyId: string; version: number; changedBy: string }) => {
      if (!data?.version) return;

      // If we have a lobby and this event is for it, check version
      if (lobby && lobby.lobbyId === data.lobbyId) {
        if (data.version > versionRef.current) {
          refresh();
        }
        return;
      }

      // If we don't have a lobby, this might be us joining one
      if (!lobby && user && data.changedBy !== user.userId) {
        return;
      }

      // We might have just joined — refetch
      if (!lobby) {
        refresh();
      }
    });

    return unsub;
  }, [subscribe, lobby, user, refresh]);

  // Listen for LOBBY_DISCORD_FAILED events
  useEffect(() => {
    const unsub = subscribe("LOBBY_DISCORD_FAILED", (data: { lobbyId: string; code: DiscordError["code"]; message: string; affectedUserIds?: string[] }) => {
      if (!lobby || data.lobbyId !== lobby.lobbyId) return;
      setDiscordError({
        code: data.code,
        message: data.message,
        affectedUserIds: data.affectedUserIds,
      });
    });

    return unsub;
  }, [subscribe, lobby]);

  // Listen for LOBBY_DISCORD_READY — channel was successfully created, refresh lobby state
  useEffect(() => {
    const unsub = subscribe("LOBBY_DISCORD_READY", (data: { lobbyId: string; channelName: string; inviteLink: string }) => {
      if (!lobby || data.lobbyId !== lobby.lobbyId) return;
      setDiscordError(null);
      refresh();
    });

    return unsub;
  }, [subscribe, lobby, refresh]);

  // Listen for LOBBY_VALIDATION_FAILED events
  useEffect(() => {
    const unsub = subscribe("LOBBY_VALIDATION_FAILED", (data: { lobbyId: string; reason: ValidationFailure["reason"]; affectedUserIds: string[] }) => {
      if (!lobby || data.lobbyId !== lobby.lobbyId) return;
      setValidationFailure({
        reason: data.reason,
        affectedUserIds: data.affectedUserIds,
      });
    });

    return unsub;
  }, [subscribe, lobby]);

  // Clear lobby on terminal states after a delay
  useEffect(() => {
    if (lobby && (lobby.state === "COMPLETED" || lobby.state === "CANCELLED")) {
      const timeout = setTimeout(() => {
        setLobby(null);
        versionRef.current = 0;
      }, 5000);
      return () => clearTimeout(timeout);
    }
  }, [lobby?.state]);

  return { lobby, loading, error, isHost, discordError, clearDiscordError, validationFailure, clearValidationFailure, refresh };
}
